import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, User } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { useAuth } from "@/lib/auth";

// Two-step signup that mirrors the mobile app 1:1:
//   1) collect name + email + password → POST /auth/register/start
//      → backend emails a 6-digit code (Resend).
//   2) collect the code → POST /auth/register/verify → JWT + user.
function Step1({ onSent }) {
  const { registerStart } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [asOwner, setAsOwner] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const data = await registerStart(email.trim().toLowerCase(), password, name.trim(), asOwner);
      toast.success("We've sent you a 6-digit code — check your inbox.");
      onSent(email.trim().toLowerCase(), data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start sign-up.";
      toast.error(detail);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="register-form-step1">
      <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
        Full name
        <input
          type="text" required minLength={1} value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full mt-2 px-4 py-3 rounded-xl border text-base font-normal normal-case tracking-normal"
          style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
          data-testid="register-name"
        />
      </label>
      <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
        Email
        <input
          type="email" required autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-2 px-4 py-3 rounded-xl border text-base font-normal normal-case tracking-normal"
          style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
          data-testid="register-email"
        />
      </label>
      <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
        Password
        <input
          type="password" required minLength={8} autoComplete="new-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full mt-2 px-4 py-3 rounded-xl border text-base font-normal normal-case tracking-normal"
          style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
          data-testid="register-password"
        />
        <span className="text-[11px] font-normal normal-case tracking-normal mt-1 block"
              style={{ color: "var(--on-surface-muted)" }}>
          Minimum 8 characters.
        </span>
      </label>
      <label className="flex items-start gap-3 text-sm cursor-pointer mt-1" style={{ color: "var(--on-surface)" }}>
        <input
          type="checkbox" checked={asOwner}
          onChange={(e) => setAsOwner(e.target.checked)}
          className="mt-0.5" data-testid="register-as-owner"
        />
        <span>I run a campsite (register with an Owner role too — you can toggle this any time in your profile)</span>
      </label>
      <button
        type="submit" disabled={loading}
        className="btn btn-green mt-2 justify-center w-full"
        data-testid="register-submit-step1"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending code…</> : <>Send verification code <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function Step2({ email, onVerified }) {
  const { registerVerify, registerResend } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(30);
  const timer = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timer.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || code.length !== 6) return;
    setLoading(true);
    try {
      const user = await registerVerify(email, code);
      toast.success(`Welcome to MyAdventure-Passport, ${user.name?.split(" ")[0] || "friend"}!`);
      onVerified(user);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Incorrect code — please try again.";
      toast.error(detail);
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await registerResend(email);
      toast.success("A new code is on its way.");
      setCooldown(30);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not resend — please try again in a moment.";
      toast.error(detail);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="register-form-step2">
      <div className="flex items-start gap-3 p-4 rounded-xl"
           style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
        <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--brand)" }} />
        <div className="text-sm" style={{ color: "var(--on-surface)" }}>
          We've emailed a 6-digit code to <b>{email}</b>. It expires in 10 minutes.
        </div>
      </div>
      <label className="text-xs font-bold uppercase tracking-widest text-center" style={{ color: "var(--on-surface-muted)" }}>
        Verification code
        <input
          type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full mt-2 px-4 py-4 rounded-xl border text-center text-2xl font-extrabold tracking-[0.5em] normal-case"
          style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
          data-testid="register-code"
        />
      </label>
      <button
        type="submit" disabled={loading || code.length !== 6}
        className="btn btn-green justify-center w-full"
        data-testid="register-submit-step2"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : <>Verify &amp; create account <ArrowRight className="w-4 h-4" /></>}
      </button>
      <button
        type="button" onClick={resend} disabled={cooldown > 0}
        className="text-xs font-bold underline underline-offset-4 mx-auto disabled:opacity-40"
        style={{ color: "var(--brand)" }}
        data-testid="register-resend"
      >
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
      </button>
    </form>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");

  return (
    <AuthShell
      title={step === 1 ? "Create your passport" : "Verify your email"}
      subtitle={step === 1 ? "Free forever. Upgrade any time for premium features." : "One last step and you're in."}
      footer={<>Already have an account? <Link to="/login" className="font-bold" style={{ color: "var(--brand)" }} data-testid="link-login">Sign in</Link></>}
    >
      {step === 1 ? (
        <Step1 onSent={(e) => { setEmail(e); setStep(2); }} />
      ) : (
        <Step2 email={email} onVerified={(user) => {
          const roles = user?.roles || [];
          const mode = user?.current_mode || (roles.includes("owner") ? "owner" : "camper");
          navigate(mode === "owner" ? "/owner" : "/passport", { replace: true });
        }} />
      )}
    </AuthShell>
  );
}
